/**
* FGA Groups List Component
*
* Displays a searchable list of groups and allows selecting or creating a group.
* Provides a sidebar-style navigation for browsing groups in the FGA manager UI.
*
* This component is responsible for:
* - Rendering the list of available groups
* - Filtering groups using a search input
* - Handling group selection
* - Opening the "Add Group" drawer panel
* - Emitting events for group selection and creation
*/

import { ChangeDetectionStrategy, EventEmitter, Component, Input, Output, signal, computed, effect, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';
import { NgkitSearchComponent } from '@wtw/angularkit/ngkit-search';
import { FgaGroup, isScimManagedGroupId } from '../../models';
import { FgaAddGroupComponent } from '../fga-add-group-panel/fga-add-group-panel.component';
import { EllipsisTitleDirective } from '../../directives/ellipsis-title.directive';
import {
  GROUP_TYPE,
  GROUP_TYPE_SUBTITLE,
  GROUPS_MANAGEMENT_FILTER,
  GROUPS_MANAGEMENT_FILTER_OPTIONS,
  GroupsManagementFilterOption,
  GroupsManagementFilterValue,
} from './fga-groups-list.model';

export interface FgaGroupRow {
  ariaLabel: string;
  id: string;
  isScimManaged: boolean;
  name: string;
  subtitle: string;
}

/**
* Features:
* - Search/filter input with reactive filtering
* - Filter between locally managed and SCIM-provisioned groups
* - Keyboard-accessible group list
* - Integration with FgaAddGroupPanel for group creation
* - Displays loading, error, and empty states
* - OnPush change detection for performance
*
* @example
* <fga-groups-list
*   [state]="groupState"
*   [selectedId]="groupState.selectedGroupId()"
*   [addingGroup]="groupState.addingGroup"
*   [addGroupError]="groupState.addGroupError"
*   [groupNames]="groupState.groupNames"
*   (selected)="handleGroupSelect($event)"
*   (addGroup)="onAddGroup($event)"
*   (addGroupInputChange)="onAddGroupInputChange()"
* ></fga-groups-list>
*/

@Component({
  selector: 'fga-groups-list',
  templateUrl: './fga-groups-list.component.html',
  styleUrls: ['./fga-groups-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, ButtonModule, SelectButton, NgkitSearchComponent, FgaAddGroupComponent, EllipsisTitleDirective]
})
export class FgaGroupsListComponent implements OnChanges {
  @Input() state: {
    groups: () => FgaGroup[];
    sortedGroups: () => FgaGroup[];
    loading: () => boolean;
    error: () => string | null;
    hasGroups: () => boolean;
  } = {
    groups: () => [],
    sortedGroups: () => [],
    loading: () => false,
    error: () => null,
    hasGroups: () => false,
  };
  @Input() selectedId: string | null = null;

  /**
   * Controls visibility of the "Create" group button.
   */
  @Input() canCreateGroup = false;

  /** Signal function indicating if a group is being added */
  @Input() addingGroup: () => boolean = () => false;

  /** Signal function exposing add group API error */
  @Input() addGroupError: () => string | null = () => null;

  /** Signal function providing existing group names for duplicate check */
  @Input() groupNames: () => string[] = () => [];

  @Output() selected = new EventEmitter<string>();

  /** Emitted when user submits a new group name */
  @Output() addGroup = new EventEmitter<string>();

  /** Emitted when user modifies the add group input (to clear API errors) */
  @Output() addGroupInputChange = new EventEmitter<void>();

  // ===========================================================================
  // Local state
  // ===========================================================================
  
  constructor () {
    effect(() => {
      const isAdding = this.addingGroup();
      const error = this.addGroupError();
      if (this.addGroupSubmitted && !isAdding && !error) {
        this.addGroupOpen = false;
        this.addGroupSubmitted = false;
      }
      if (this.addGroupSubmitted && !isAdding && error) {
        this.addGroupSubmitted = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedId']?.currentValue) {
      requestAnimationFrame(() => {
        const groupList = document.querySelector('.groups-panel__items');
        const selectedItem = groupList?.querySelector(`[data-group-id="${this.selectedId}"]`);
        selectedItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth', inline: 'nearest' });
      });
    }
  }

  addGroupOpen = false;
  addGroupSubmitted = false;

  readonly filterText = signal<string>('');

  readonly managementFilter = signal<GroupsManagementFilterValue>(GROUPS_MANAGEMENT_FILTER.ALL);

  readonly managementFilterOptions: GroupsManagementFilterOption[] = GROUPS_MANAGEMENT_FILTER_OPTIONS;

  readonly filteredGroups = computed<FgaGroup[]>(() => {
    const term = this.filterText().trim().toLowerCase();
    const sortedGroups: FgaGroup[] = this.state.sortedGroups?.() ?? [];
    const groups: FgaGroup[] = sortedGroups.length > 0
      ? sortedGroups
      : (this.state.groups?.() ?? []);

    const scoped = this.scopeToManagementFilter(groups);

    if (!term) return scoped;
    return scoped.filter(g => (g.name ?? '').toLowerCase().includes(term));
  });

  readonly groupRows = computed<FgaGroupRow[]>(() =>
    this.filteredGroups().map(group => {
      const isScimManaged = isScimManagedGroupId(group.id);
      const type = isScimManaged ? GROUP_TYPE.SCIM : GROUP_TYPE.LOCAL;
      const subtitle = GROUP_TYPE_SUBTITLE[type];

      return {
        ariaLabel: `Open group ${group.name}, ${subtitle.toLowerCase()}`,
        id: group.id,
        isScimManaged,
        name: group.name,
        subtitle,
      };
    })
  );

  /** True once the groups have loaded but the active filters match nothing. */
  readonly hasNoMatches = computed<boolean>(
    () => this.state.hasGroups?.() === true && this.filteredGroups().length === 0
  );

  // ===========================================================================
  // Handlers
  // ===========================================================================

  onSelect(id: string): void {
    this.selected.emit(id);
  }

  onSearch(value: string): void {
    this.filterText.set(value ?? '');
  }

  clearSearch(): void {
    this.filterText.set('');
  }

  onManagementFilterChange(value: GroupsManagementFilterValue | null | undefined): void {
    this.managementFilter.set(value ?? GROUPS_MANAGEMENT_FILTER.ALL);
  }

  onAddGroup(name: string): void {
    this.addGroupSubmitted = true;
    this.addGroup.emit(name);
  }

  onOpenAddGroup(): void {
    this.addGroupOpen = true;
  }

  onCloseAddGroup(): void {
    this.addGroupOpen = false;
  }

  private scopeToManagementFilter(groups: FgaGroup[]): FgaGroup[] {
    const filter = this.managementFilter();
    if (filter === GROUPS_MANAGEMENT_FILTER.ALL) return groups;

    const wantScim = filter === GROUPS_MANAGEMENT_FILTER.SCIM;
    return groups.filter(g => isScimManagedGroupId(g.id) === wantScim);
  }
}